import { useCallback, useEffect, useMemo, useState } from "react"
import { GetApp } from "../App"
import { accountSessionIdentity } from "../Data/AccountSession"
import { GetShipContext } from "../App/Context"
import { useSubscription } from "../App/useSubscription"
import { logger } from "../App/Logger"
import { apiErrorReason } from "../App/ApiError"
import { iapPurchaseEnabled, subscriptionRendersInApp } from "../App/IAP"
import { BlockContainer, BlockField, BlockRow } from "../Components/Block"
import { CloseButton } from "../Components/CloseButton"
import { Icons } from "../Components/Icon"
import { LocalizedMessage } from "../Components/LocalizedMessage"
import { TabBar } from "../Components/TabBar"
import { AccountApi, IAccountUserLevel } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { AppRoutes, GetAppView } from "./AppView"
import { AccountSettingsAccountView } from "./AccountSettings/AccountView"
import { AccountSettingsEditAccount, AccountSettingsViewData, takeCompanionReconnectNotice } from "./AccountSettings/Context"
import { AccountSettingsViewProps } from "./AccountSettings/Params"
import { AccountSettingsPlanView } from "./AccountSettings/PlanView"
import { AccountSettingsShipsView } from "./AccountSettings/ShipsView"
import { AccountSettingsUsersView } from "./AccountSettings/UsersView"
import { useAccountUsers } from "./AccountSettings/useAccountUsers"

interface ILocalShip {
  id: number;
}

export function AccountSettingsView({ cancelEdit, ships, companionReconnect: companionReconnectOverride, companionShipId, companionDisconnectState, initTab }: AccountSettingsViewProps) {
  const app = GetApp();
  const { apiUser } = GetShipContext();
  const [accountUsersRequestVersion, setAccountUsersRequestVersion] = useState(0);
  const [accountPlan, setAccountPlan] = useState<string | undefined>(undefined);
  const [editAccountError, setEditAccountError] = useState<string | undefined>(undefined);
  const [editAccount, setEditAccount] = useState<AccountSettingsEditAccount>(AccountSettingsEditAccount.none);
  // Ship ids currently advertised on the LAN. Updated from the local-ship broadcasts,
  // which arrive independently of renders -- so every update is functional and
  // deduplicated. The previous `setLocalShips([...localShips, ship.id])` closed over a
  // stale array, so two broadcasts landing before a re-render lost one, and repeated
  // `create` broadcasts for the same ship appended duplicates. `update` (a re-resolved
  // advertisement) was ignored entirely, so a ship that changed address never appeared.
  const [nearbyShipIds, setNearbyShipIds] = useState<number[] | undefined>(undefined);
  // Whether this device landed here because a stored companion pairing could not be
  // reached. Read once, at the edge, so the views below stay functions of their props
  // and the flag can be rendered from a catalog fixture (#1388).
  const [companionReconnect] = useState<boolean>(() => companionReconnectOverride ?? takeCompanionReconnectNotice());
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { loadShips, navigateApp } = GetAppView();
  // account.plan()/account.delete() are the real predicates behind the subscription
  // info block and the remove-account button — replaces earlier hand-rolled presence
  // checks with the generated permission matrix (#931 Phase G). The subscription
  // TAB itself stays gated on session-type (`user.accountId`/`app.localSession()`)
  // since it also hosts account-name editing, which isn't behind accountLevel().
  const accountPlanAvailable = useMemo(() => new AccountApi(app.apiClient).account.plan({ params: {} }).available(apiUser), [app.apiClient, apiUser]);
  const accountDeletable = useMemo(() => new AccountApi(app.apiClient).account.delete({ params: {} }).available(apiUser), [app.apiClient, apiUser]);
  const accountUsersAvailable = useMemo(() => new AccountApi(app.apiClient).account.users({ params: {} }).available(apiUser), [app.apiClient, apiUser]);
  // The account-level select only ever offers user/admin -- disabled is exclusively
  // reachable through the deactivate/reactivate menu actions on each row, never a
  // dropdown choice (mirrors ShipDataView's shipUserLevelOptions convention).
  const accountUserLevelOptions = useMemo(() => {
    const tt = app.getT("AccountSettingsView");
    return [IAccountUserLevel.user, IAccountUserLevel.admin].map((level) => (
      <option value={level} key={level}>{AccountSettingsViewData.accountUserLevelText(tt, level)}</option>
    ));
  }, []);
  const t = app.getT("AccountSettingsView");

  useSubscription((ship: ILocalShip, _type: string, info: string) => {
    if (info == "create" || info == "update") {
      // `update` is a re-advertised/re-resolved service for a ship already known --
      // idempotent here, but it must still ADD a ship first seen via an update.
      setNearbyShipIds((previous) => {
        const current = previous || [];
        return current.indexOf(ship.id) >= 0 ? current : [...current, ship.id];
      });
    } else if (info == "delete") {
      setNearbyShipIds((previous) => previous?.filter((id) => id != ship.id));
    }
  }, Subscription.LocalShip);

  const user = app.user || { accountId: undefined, email: "", name: "", shortname: "" };
  const account = app.account;
  const accountUserInfos = useAccountUsers(
    user.accountId,
    accountUsersRequestVersion > 0,
    accountUsersRequestVersion,
  );

  // iOS in-app-purchase mode: on in the native iOS app; in a cloud/browser session
  // it stays behind the `logbook-inapp` opt-in. Computed inline (not a one-shot
  // useMemo) because `appStyles` is populated asynchronously from the native bridge
  // after mount, so a frozen memo would capture the pre-bridge value.
  const inApp = iapPurchaseEnabled(app);
  const separateSharedShips = app.hasFeature("multi-user");

  useEffect(() => {
    if (!ships) {
      loadShips();
    }
    if (!nearbyShipIds && app.hasFeature("bonjour")) {
      const load = async () => {
        const response = await AccountSettingsViewData.loadLocalShips(app.apiClient);
        if (response && response.ships) {
          setNearbyShipIds(response.ships);
        }
      };
      load().catch(console.error);
    }
  }, [ships]);

  // Tab-click load: only fetches once (accountUserInfos is only populated once this
  // tab is opened).
  const loadAccountUsers = useCallback(async () => {
    setAccountUsersRequestVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    if (initTab === "tab_users") {
      loadAccountUsers().catch(console.error);
    }
  }, [initTab, loadAccountUsers]);

  const loadAccountPlan = useCallback(async () => {
    if (accountPlan) {
      return;
    }
    const data = await AccountSettingsViewData.loadAccountPlan(app.apiClient);
    if (data.plan) {
      setAccountPlan(data.plan.name);
    }
  }, [accountPlan]);

  const tabSelected = useCallback((value?: string) => {
    if (value == "tab_users") {
      loadAccountUsers();
    // } else if (value == "tab_ship_users") {
    //   loadShipUsers();
    }
    if (value == "tab_subscription" && app.cloudSession() && accountPlanAvailable) {
      loadAccountPlan();
    }
  }, [accountPlanAvailable]);

  const showPlans = useCallback(() => {
    // The subscription/plan view renders in-app for cloud and native iOS (so the
    // StoreKit purchase can run); only a non-iOS native app opens it on the server.
    if (subscriptionRendersInApp(app)) {
      navigateApp(AppRoutes.accountSubscription);
    } else {
      app.openServer("account/subscription");
    }
  }, [inApp]);

  const editAccountAction = useCallback(() => {
    if (app.cloudSession()) {
      setEditAccount(AccountSettingsEditAccount.name);
    } else {
      if (inApp) {
        app.openServer("account?inapp=true");
      } else {
        app.openServer("account");
      }
    }
  }, [inApp]);

  const accountShips = useMemo(() => {
    return AccountSettingsViewData.accountShips(ships, separateSharedShips ? account : undefined);
  }, [ships, account, separateSharedShips]);

  const sharedShips = useMemo(() => {
    return AccountSettingsViewData.sharedShips(ships, separateSharedShips ? account : undefined);
  }, [ships, account, separateSharedShips]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveAccountUser = useCallback(async (_data: FormData, values: any) => {
    // Never log `values` — this form doubles as the password change form
    // (issue #1273); the action alone is enough of a trail.
    logger.debug("saveAccountUser", AccountSettingsEditAccount[editAccount]);
    setEditAccountError(undefined);
    const request = editAccount == AccountSettingsEditAccount.password
      ? AccountSettingsViewData.updatePassword(app.apiClient, values)
      : AccountSettingsViewData.updateAccountUser(app.apiClient, values);
    const requestIdentity = accountSessionIdentity(app.session, app.sessionType, app.apiClient.target);
    const result = await request.catch((err) => {
      // Named or not, the failure has to be visible (#1820): the old `err["error"] == true`
      // guard tested a wire shape the server stopped sending, so a wrong current password
      // silently did nothing at all.
      logger.debug("saveAccountUser failed");
      setEditAccountError(apiErrorReason(err) || "account_update_failed");
      return undefined;
    });
    if (result) {
      app.reconcileSession(result, requestIdentity);
      setEditAccount(AccountSettingsEditAccount.none);
    }
  }, [editAccount]);

  return (
    <div
      className="AccountSettingsView"
      data-catalog-ready={initTab === "tab_users" ? (accountUserInfos ? "true" : "false") : undefined}
    >
      <h2>{t("account_settings")}</h2>

      {/* info block at the top when on smartphone */}
      {!app.hasFeature("primary") && app.primarySession() && (
        <BlockRow className="message block_field_small_50">
          <BlockField>
            <LocalizedMessage html={true} message={(m) => m.noteText.shipsSmartphone} />
          </BlockField>
        </BlockRow>
      )}

      {/* info block at the top when no ship yet */}
      {accountShips && accountShips.length === 0 && (
        <BlockRow className="message block_field_small_50">
          <BlockField>
            <LocalizedMessage html={true} message={(m) => m.noteText.firstLogin} />
            {app.cloudSession() && <LocalizedMessage html={true} message={(m) => m.noteText.firstLoginCloud} />}
          </BlockField>
        </BlockRow>
      )}

      {!app.cloudSession() && app.shipId && !companionDisconnectState && (
        <BlockField>
          <CloseButton key={"close"} onClick={cancelEdit} className="CloseButton_right" />
        </BlockField>
      )}
      <TabBar defaultSelection={initTab ?? "tab_ships"}
        tabs={[

          // all ships, new ship, import ship
          { icon: Icons.SailingVessel, name: "tab_ships", title: t("tab_ships"), render: () =>
            <AccountSettingsShipsView ships={ships} accountShips={accountShips} sharedShips={sharedShips} nearbyShipIds={nearbyShipIds} cancelEdit={cancelEdit} companionReconnect={companionReconnect} companionShipId={companionShipId} companionDisconnectState={companionDisconnectState} /> },

          // Infos to current account, links to Account on server and Plan on server
          { icon: Icons.User, hide: user.accountId == undefined || app.localSession(), name: "tab_subscription", title: t("tab_subscription"), render: () => (
            <>
              <BlockContainer className="ShipContentView-empty-select  main-page-containers">
                <AccountSettingsAccountView
                  editAccount={editAccount}
                  setEditAccount={setEditAccount}
                  editAccountError={editAccountError}
                  editAccountAction={editAccountAction}
                  saveAccountUser={saveAccountUser}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  accountDeletable={accountDeletable}
                />
                <AccountSettingsPlanView accountPlan={accountPlan} available={accountPlanAvailable} showPlans={showPlans} />
              </BlockContainer>
            </>
          ) },

          // List of all users for the account — gated on the viewing user's own
          // accountLevel via the generated account.users() predicate (same
          // mechanism as accountPlanAvailable/accountDeletable above), not the
          // loaded accountUserInfos (which is only fetched once this tab is
          // opened, so gating on it would be a chicken-and-egg problem).
          { hide: !app.hasFeature("multi-user") || !app.cloudSession() || user.accountId == undefined || !accountUsersAvailable,
            icon: Icons.Account, name: "tab_users", title: t("tab_users"), render: () => (
              <AccountSettingsUsersView
                accountUserInfos={accountUserInfos}
                accountUserLevelOptions={accountUserLevelOptions}
                onChanged={() => undefined}
              />
            ) },

        ]} selectedCallback={tabSelected}
      />

    </div>
  );
}

// Re-export the shared public API so existing `from "./AccountSettingsView"` imports keep working.
export { AccountSettingsEditAccount, AccountSettingsViewData } from "./AccountSettings/Context"
